import type { ShareRole } from "@hector/shared-types/resource-sharing";
import type { EnrichedCabinetMember } from "@/hooks/cabinet/types";

/**
 * Partage nominatif tel qu'affiché/produit par `ShareControl` et piloté par
 * `useShareController` (hors owner). Source unique du type — importée par le
 * composant ET le hook pour éviter toute divergence de contrat.
 */
export interface ShareGrant {
  user_id: string;
  role: ShareRole;
}

/**
 * Destinataires ajoutables par le combobox « Ajouter une personne » de
 * `ShareControl` : membres du cabinet, l'owner exclu (propriété déjà acquise)
 * et tout membre déjà partagé exclu (pas de doublon de partage — le backend
 * le rejetterait de toute façon, mais l'UI ne doit même pas le proposer).
 *
 * Pure — partagée par les 3 ressources partageables (dossier / conversation /
 * modèle) consommant `ShareControl`.
 */
export function filterAddableMembers(
  members: EnrichedCabinetMember[] | undefined,
  shares: Array<{ user_id: string }>,
  ownerId: string,
): EnrichedCabinetMember[] {
  const sharedUserIds = new Set(shares.map((s) => s.user_id));
  return (members ?? []).filter(
    (m) => m.user_id !== ownerId && !sharedUserIds.has(m.user_id),
  );
}

/**
 * Options de rôle affichées dans le `<Select>` d'une ligne de partage
 * nominatif. Libellés produit verrouillés : « Lecture » / « Édition »
 * (jamais « Lecteur »/« Éditeur ») — cf. brief C1.
 */
export const PERSON_ROLE_OPTIONS: ReadonlyArray<{ value: ShareRole; label: string }> = [
  { value: "viewer", label: "Lecture" },
  { value: "editor", label: "Édition" },
];

/**
 * Filtre les membres ajoutables par la barre de recherche inline de
 * `ShareControl` (nom OU email, insensible à la casse). Pure — testée
 * indépendamment du rendu.
 */
export function filterMembersByQuery(
  members: EnrichedCabinetMember[],
  query: string,
): EnrichedCabinetMember[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return members;
  return members.filter(
    (m) =>
      (m.full_name ?? "").toLowerCase().includes(normalized) ||
      (m.email ?? "").toLowerCase().includes(normalized),
  );
}

/**
 * L'entrée « Tout le cabinet » du panneau de recherche reste proposée tant
 * que la requête est vide ou qu'elle matche son libellé — évite qu'elle
 * disparaisse dès qu'on tape le nom d'une personne.
 */
export function cabinetOptionMatchesQuery(query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return "tout le cabinet".includes(normalized) || "cabinet".includes(normalized);
}
